import React, { useMemo, useState } from 'react'

import { PageType } from '@makyoapp/makyo-sdk/models/AppBuilder'
import {
  ActionFlag,
  BuilderPage,
  TopBarType,
  VariableResultMenu,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { MaterialIcon, TopAppBar, useRtl } from '@makyoapp/makyo-tw-ui'

import { IAppPage } from '@/core/app-builder/app/page'
import { parsePageTitle } from '@/core/app-builder/parser/variable-text'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { useAppRoutingContext } from '@/context/__project__/AppRoutingContext'
import { useBuilderActionContextV2 } from '@/context/__project__/BuilderActionContextV2'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useCheckVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useActiveDevice } from '@/hooks/useActiveDevice'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { LeadingSidebarTab } from '@/shared/builder-ui'
import { THUMB_AVATAR } from '@/shared/constants'
import { parseLabel } from '@/shared/utils'

import { useGetBadgeContent } from './hooks/useGetBadgeContent'
import { TopBarCheckedMode } from './TopBarCheckedMode'

export interface BuilderTopBarCoreProps {
  page: BuilderPage
  pageClient: IAppPage
}

/**
 * BuilderTopBarCore
 *
 * @todo:
 * - leading element & trailing element
 * - checked items
 * - handle search
 */
export const BuilderTopBarCore = ({ page, pageClient }: BuilderTopBarCoreProps) => {
  // access global contexts
  const { language, setLanguage, languages, userSpecific } = useDbClientContext()
  const {
    pages,
    menus,
    listChecked,
    topBarColor,
    topBarVisibility,
    topBarShowTitle,
    topBarShowIcon,
    isDevelopmentMode,
    setMenuItemAction,
    setMenuAction,
    setActiveTab,
    setPageAction,
    showLanguageSwitcher,
    showThemeSwitcher,
    appClient,
  } = useAppBuilderContext()

  // Local state for dropdown
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)
  const { currentUser } = useAppAuthContext()
  const { handleGoBack, currentRowId } = useAppRoutingContext()
  const { simulateAction } = useBuilderActionContextV2()
  const { activePageTableId, activePageRowIndex, activeLayerDetailRowId } = useActiveLayerContext()
  const { checkVisibility } = useCheckVisibilityV2()
  const { goToPageSafely } = useAppPagesContext()

  const dir = useRtl()
  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  const currentTopBarColor = topBarColor[mode]

  // breakpoint
  const { isNavigationBarTop } = useBreakpoint()
  const { getBadgeContent } = useGetBadgeContent()

  // access device
  const [device] = useActiveDevice()

  // access page title
  const pageTitle = parsePageTitle(pageClient, language, userSpecific, currentRowId)

  // access use global setting
  const useGlobalSetting = pageClient.settings._TopBar._UseGbSettings || false

  // check if page is detail page
  const isDetailPage = !!page?.tableId

  const trailingPageMenu = page.tbTrailingElements[0] as VariableResultMenu
  const pageMenu = menus.find(({ id }) => id === trailingPageMenu?.value)

  const shouldTopAppBarShown = checkVisibility({ visibility: pageMenu?.visibility })

  const leading = useMemo(() => {
    if (isDetailPage) return undefined

    const leadingMenu = page.tbLeadingElementLogOut as VariableResultMenu
    const idMenu = leadingMenu?.value
    const menu = menus.find((m) => m.id === idMenu)
    const theIcon = (menu?.items[0]?.icon as any)?.value
    return {
      icon: theIcon ? <MaterialIcon icon={theIcon} size={24} /> : undefined,
      onClick: () => {
        simulateAction({
          actions: menu?.items?.[0]?.actions || [],
          rowIndex: activePageRowIndex,
          tableId: activePageTableId,
          rowId: activeLayerDetailRowId,
          // TODO handle checklist
        })
      },
    }
  }, [page, isDetailPage, menus, simulateAction, activePageRowIndex, activePageTableId, activeLayerDetailRowId])

  // // Theme Switcher Component
  // const themeSwitcher = useMemo(() => {
  //   if (!showThemeSwitcher) return undefined

  //   const currentScheme = scheme.current
  //   const savedScheme = scheme.saved
  //   const icon = currentScheme === 'dark' ? 'dark_mode' : 'light_mode'
  //   const tooltipText = currentScheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'

  //   const handleToggleTheme = () => {
  //     let newScheme: string
  //     if (savedScheme === 'match-device') {
  //       newScheme = currentScheme === 'dark' ? 'light' : 'dark'
  //     } else {
  //       newScheme = savedScheme === 'dark' ? 'light' : 'dark'
  //     }
  //     appClient.UpdateProjectColorScheme(newScheme)
  //   }

  //   return (
  //     <Tooltip content={tooltipText}>
  //       <MaterialIcon icon={icon} size={24} className='cursor-pointer' onClick={handleToggleTheme} />
  //     </Tooltip>
  //   )
  // }, [showThemeSwitcher, scheme, appClient])

  // // Language Switcher Component
  // const languageSwitcher = useMemo(() => {
  //   if (!showLanguageSwitcher || languages.length <= 1) return undefined

  //   const currentLanguage = getLanguage(language)
  //   const flagEmoji = currentLanguage?.flag?.emoji || '🌐'
  //   const languageName = currentLanguage?.name || 'Language'

  //   return (
  //     <DD
  //       show={showLanguageDropdown}
  //       onToggle={(nextShow) => setShowLanguageDropdown(nextShow)}
  //       toggleNoButton
  //       placement='bottom-end'
  //       menuClassName='min-w-[200px] max-h-[300px] overflow-y-auto z-[9999] rounded'
  //       toggle={(_active, toggleProps) => {
  //         return (
  //           <Tooltip content={`Change Language (${languageName})`}>
  //             <div {...toggleProps} className='cursor-pointer text-2xl leading-none flex items-center'>
  //               {flagEmoji}
  //             </div>
  //           </Tooltip>
  //         )
  //       }}
  //     >
  //       <div className='py-1'>
  //         {languages.map((lng) => (
  //           <button
  //             key={lng.ids.locale}
  //             onClick={() => {
  //               setLanguage(lng.ids.locale)
  //               setShowLanguageDropdown(false)
  //             }}
  //             className={cn(
  //               'w-full px-3 py-2 text-left flex items-center gap-2 cursor-pointer',
  //               'text-zinc-900 dark:text-slate-100',
  //               'bg-white dark:bg-slate-800',
  //               'hover:bg-zinc-100 dark:hover:bg-slate-700',
  //               language === lng.ids.locale && 'bg-zinc-200 dark:bg-slate-600'
  //             )}
  //           >
  //             <CountryOption slug={lng.countryCode} name={lng.name} />
  //           </button>
  //         ))}
  //       </div>
  //     </DD>
  //   )
  // }, [showLanguageSwitcher, languages, showLanguageDropdown, language, setLanguage])

  const trailing = useMemo(() => {
    const userProfilePage = pages.find((p) => p.type === PageType.UserProfile)
    const userProfileTrailingMenu = (userProfilePage?.tbTrailingElements[0] ?? []) as VariableResultMenu
    const trailingMenu = useGlobalSetting ? userProfileTrailingMenu : trailingPageMenu
    if (!trailingMenu) return []

    const menu = menus.find(({ id }) => id === trailingMenu?.value)
    if (!menu || !shouldTopAppBarShown) return []

    const hasIconOrImage = menu?.items.some((m) => {
      const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
      const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
      const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image
      return isIcon || isAvatar || isImage
    })

    const actionButtons = menu.items
      .filter((item) => checkVisibility({ visibility: item.visibility }))
      .map((m) => {
        const isIcon = m?.icon?.flag === VariableSelectFeatureFlag.Icon
        const isAvatar = m?.icon?.flag === VariableSelectFeatureFlag.Avatar
        const isImage = m?.icon?.flag === VariableSelectFeatureFlag.Image

        let media: { icon?: React.ReactNode | undefined; image?: string | undefined } = {
          icon: hasIconOrImage ? <div className='w-6 h-6 visible-hidden' /> : undefined,
          image: undefined,
        }

        if (isIcon) {
          media = { icon: <MaterialIcon icon={(m.icon as any)?.value} size={24} /> }
        } else if (isAvatar) {
          media = { image: currentUser?.photo || THUMB_AVATAR, icon: undefined }
        } else if (isImage) {
          media = { image: (m.icon as any)?.value, icon: undefined }
        }

        const badge = getBadgeContent(m)

        let theBadge = undefined

        if (badge.showBadge) {
          if (badge.badgeSize === 's') {
            theBadge = null
          } else {
            theBadge = badge.badgeValue
          }
        }

        return {
          ...media,
          label: parseLabel(m.title),
          badgeText: theBadge,
          onClick: () => {
            // TODO: Handle checklist
            // const checkedListTable = listChecked?.extraActionProps.table as TableAdapter | undefined
            // const isMultiple = !!listChecked && !!checkedListTable && !!listChecked.items.length
            // const multipleOptTableId = checkedListTable?.id

            setActiveTab(LeadingSidebarTab.Menu)

            setMenuAction({
              flag: ActionFlag.Edit,
              menu: menu,
            })

            setMenuItemAction({
              flag: ActionFlag.Edit,
              menuItemId: m.id,
            })

            if (isDevelopmentMode) return

            if (m.actions && m.actions?.length > 0) {
              simulateAction({
                actions: m.actions,
                rowIndex: activePageRowIndex,
                tableId: activePageTableId,
                rowId: activeLayerDetailRowId,
                // TODO handle checklist
              })
            }
          },
        }
      })

    return actionButtons
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeLayerDetailRowId,
    activePageRowIndex,
    activePageTableId,
    checkVisibility,
    currentUser?.photo,
    getBadgeContent,
    pageMenu,
    pages,
    shouldTopAppBarShown,
    simulateAction,
    isDevelopmentMode,
  ])

  let appbarPosClass = ''

  if (!isNavigationBarTop) {
    appbarPosClass = 'sticky! top-0 left-0 z-50'
  }

  // Render Checked Mode
  const isCheckedMode = listChecked && listChecked.items.length > 0
  if (isCheckedMode) {
    return <TopBarCheckedMode color={currentTopBarColor} page={page} />
  }

  // Render Topbar
  if (page.tbType === TopBarType.AppBar) {
    if (!topBarVisibility) {
      return null
    }

    const maxVisibleButtons = device === 'phone' ? 3 : 4
    const showIcon = page.icon && topBarShowIcon && !isDetailPage

    const handleIconTitleClick = () => {
      setPageAction({
        flag: ActionFlag.Edit,
        page: page.id,
      })
      if (page.slug) {
        goToPageSafely(page.slug)
      }
    }

    return (
      <TopAppBar
        dir={dir}
        className={appbarPosClass}
        backgroundColor={currentTopBarColor}
        onMenuClick={leading?.onClick}
        onBackClick={handleGoBack}
        maxVisibleButtons={maxVisibleButtons}
        showBackButton={isDetailPage}
        title={page.tbPageTitle && topBarShowTitle ? pageTitle : ''}
        leadingIcon={leading?.icon}
        icon={showIcon ? <MaterialIcon icon={page.icon} size={24} /> : undefined}
        actionButtons={trailing}
        onIconTitleClick={handleIconTitleClick}
        // theme={themeSwitcher}
        // language={languageSwitcher}
      />
    )
  }
  return null
}
